/* Sign in.
 *
 * The one screen someone sees before they are anyone. It says the shop's name,
 * takes an email and a password, and — because there is no public signup, ever
 * (IDEA.md §9) — explains that the way in is an invitation, so nobody hunts for
 * a "create account" link that will never exist.
 *
 * Inputs are 16px so iOS doesn't zoom on focus, and 48px tall for gloves. */
import { useState, type FormEvent } from 'react';
import { useApp } from '@/store/useApp';
import { PasswordField } from '@/ui/controls';

export function LoginScreen() {
  const signIn = useApp((s) => s.signIn);
  const signingIn = useApp((s) => s.signingIn);
  const authError = useApp((s) => s.authError);
  const authNotice = useApp((s) => s.authNotice);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    void signIn(email.trim(), password);
  };

  return (
    /* The document does not scroll — the app is a frame (components.css) — so
       this screen is its own scrollport rather than a page that grows. The form
       is centred by `margin: auto` on the card, not by `align-items`: on a
       short screen with the keyboard up, a centred flex item taller than its
       container loses its top edge off the top of the scroll. */
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        background: 'var(--ground)',
        display: 'flex',
        padding: 24,
      }}
    >
      <main style={{ width: 'min(420px, 100%)', margin: 'auto' }}>
        <div style={{ marginBottom: 22 }}>
          <div
            style={{
              fontFamily: 'var(--font-heading)',
              fontWeight: 600,
              fontSize: 22,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              color: 'var(--ink)',
            }}
          >
            OneShot 2Pac
          </div>
          <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--ink-muted)' }}>
            Sign in to see what&rsquo;s in the shop.
          </p>
        </div>

        <form
          className="blueprint"
          onSubmit={handleSubmit}
          style={{
            background: 'var(--surface)',
            padding: 20,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {authNotice ? (
            <p
              role="status"
              style={{
                margin: 0,
                padding: '10px 12px',
                borderRadius: 10,
                background: 'var(--line-soft)',
                border: '1px solid var(--line)',
                color: 'var(--ink)',
                fontSize: 13.5,
                lineHeight: 1.45,
              }}
            >
              {authNotice}
            </p>
          ) : null}

          {authError ? (
            <p
              role="alert"
              style={{
                margin: 0,
                padding: '10px 12px',
                borderRadius: 10,
                background: 'var(--urgent-tint)',
                border: '1px solid var(--urgent)',
                color: 'var(--ink)',
                fontSize: 13.5,
                lineHeight: 1.45,
              }}
            >
              {authError}
            </p>
          ) : null}

          <label className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="label-xs">Email</span>
            <input
              className="input"
              type="email"
              name="email"
              autoComplete="username"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@1shot2pac.com.au"
              style={{ height: 48, fontSize: 16 }}
            />
          </label>

          <PasswordField
            label="Password"
            name="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            required
          />

          <button
            type="submit"
            className="btn btn-primary blueprint"
            disabled={signingIn}
            style={{ height: 48, fontSize: 15, justifyContent: 'center' }}
          >
            {signingIn ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p
          style={{
            margin: '14px 2px 0',
            fontSize: 12.5,
            lineHeight: 1.5,
            color: 'var(--ink-muted)',
          }}
        >
          New to the shop? The owner sends an invitation — there is no sign-up. Lost your password?
          Ask the office to send you a new invitation.
        </p>
      </main>
    </div>
  );
}

/** Shown while the session call is in flight, so a signed-in person never sees
 *  the sign-in form flash past on a refresh. */
export function CheckingSession() {
  return (
    <div
      style={{
        height: '100%',
        background: 'var(--ground)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <p role="status" style={{ margin: 0, fontSize: 14, color: 'var(--ink-muted)' }}>
        Checking your session&hellip;
      </p>
    </div>
  );
}
